import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, Mic, RotateCcw, Volume2 } from "lucide-react";
import {
  EM_ARPEGGIO_STEPS,
  getDiagramForMission,
  type ChordPathMission,
} from "../../../lib/chords/chordPathPlan";
import {
  noteOctaveMatches,
  scoreChordStrum,
  type StrumScoreResult,
} from "../../../lib/chords/chordPathEngine";
import { ModuleIntroModal } from "../../module/ModuleIntroModal";
import { SongLearnCanvasBackdrop } from "../../SongLearnCanvasBackdrop";
import { PracticeSurface } from "../../practice/PracticeSurface";
import { StudioTunerControl } from "../../StudioTunerControl";
import { useGuitarSynth } from "../../../hooks/useGuitarSynth";
import { usePolyPitchDetector } from "../../../hooks/usePolyPitchDetector";
import { usePitchDetector } from "../../../hooks/usePitchDetector";
import { BEGINNER_PATH_STUDIO_NECK_CLASS } from "../../../lib/beginnerPath/beginnerPathLayout";
import { BEGINNER_PATH_THEME as PATH } from "../../../lib/beginnerPath/beginnerPathTheme";
import type { NoteName } from "../../../lib/guitarEngine";

type Phase = "intro" | "practice" | "result";

export interface ChordPathMissionStudioProps {
  mission: ChordPathMission;
  wasCompletedBefore: boolean;
  pathMode?: boolean;
  onBack: () => void;
  onComplete: () => void;
}

export function ChordPathMissionStudio({
  mission,
  wasCompletedBefore,
  pathMode = false,
  onBack,
  onComplete,
}: ChordPathMissionStudioProps) {
  const [phase, setPhase] = useState<Phase>("intro");
  const [passed, setPassed] = useState(false);
  const [strumScore, setStrumScore] = useState<StrumScoreResult | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [micError, setMicError] = useState<string | null>(null);
  const [arpeggioIndex, setArpeggioIndex] = useState(0);
  const [arpeggioHits, setArpeggioHits] = useState<boolean[]>([]);
  const [listeningArpeggio, setListeningArpeggio] = useState(false);

  const diagram = useMemo(() => getDiagramForMission(mission), [mission]);
  const { playChord, playFret } = useGuitarSynth();
  const {
    state: polyState,
    loadModel,
    analyzeFromMicrophone,
  } = usePolyPitchDetector();

  const arpeggioIndexRef = useRef(0);
  const listeningRef = useRef(false);
  const advanceLockRef = useRef(false);

  const onArpeggioNote = useCallback(
    (note: NoteName, octave: number) => {
      if (!listeningRef.current || advanceLockRef.current) return;
      const step = EM_ARPEGGIO_STEPS[arpeggioIndexRef.current];
      if (!step) return;
      if (!noteOctaveMatches(note, octave, step.note, step.octave)) return;

      advanceLockRef.current = true;
      const idx = arpeggioIndexRef.current;
      setArpeggioHits((prev) => {
        const next = [...prev];
        next[idx] = true;
        return next;
      });

      const nextIdx = idx + 1;
      if (nextIdx >= EM_ARPEGGIO_STEPS.length) {
        listeningRef.current = false;
        setListeningArpeggio(false);
        setPassed(true);
        setPhase("result");
        advanceLockRef.current = false;
        return;
      }
      arpeggioIndexRef.current = nextIdx;
      setArpeggioIndex(nextIdx);
      window.setTimeout(() => {
        advanceLockRef.current = false;
      }, 350);
    },
    []
  );

  const { startListening, stopListening } = usePitchDetector({
    threshold: 0.004,
    holdTimeMs: 140,
    onNoteDetected: onArpeggioNote,
  });

  useEffect(() => {
    setPhase("intro");
    setPassed(false);
    setStrumScore(null);
    setArpeggioIndex(0);
    setArpeggioHits([]);
    arpeggioIndexRef.current = 0;
    listeningRef.current = false;
    setListeningArpeggio(false);
    stopListening();
  }, [mission.id, stopListening]);

  useEffect(() => {
    return () => {
      listeningRef.current = false;
      stopListening();
    };
  }, [stopListening]);

  const handleListenSynth = useCallback(() => {
    playChord(diagram.playNotes, 1400, 28);
  }, [diagram.playNotes, playChord]);

  const handleStrumDetect = useCallback(async () => {
    setMicError(null);
    setStrumScore(null);
    setIsRecording(true);
    try {
      if (!polyState.isReady && !polyState.isLoading) {
        await loadModel();
      }
      const events = await analyzeFromMicrophone(2200, true);
      const score = scoreChordStrum(mission, events);
      setStrumScore(score);
      if (score.passed) {
        setPassed(true);
        setPhase("result");
      }
    } catch {
      setMicError("No se pudo usar el micrófono. Revisa permisos del navegador.");
    } finally {
      setIsRecording(false);
    }
  }, [
    analyzeFromMicrophone,
    loadModel,
    mission,
    polyState.isLoading,
    polyState.isReady,
  ]);

  const startArpeggio = useCallback(async () => {
    setMicError(null);
    setArpeggioIndex(0);
    setArpeggioHits(Array(EM_ARPEGGIO_STEPS.length).fill(false));
    arpeggioIndexRef.current = 0;
    advanceLockRef.current = false;
    setPhase("practice");
    try {
      await startListening();
      listeningRef.current = true;
      setListeningArpeggio(true);
    } catch {
      setMicError("No se pudo abrir el micrófono. Revisa permisos del navegador.");
      listeningRef.current = false;
      setListeningArpeggio(false);
    }
  }, [startListening]);

  const stopArpeggio = useCallback(() => {
    listeningRef.current = false;
    setListeningArpeggio(false);
    stopListening();
  }, [stopListening]);

  const beginPractice = useCallback(() => {
    setPhase("practice");
    if (mission.evalMode === "arpeggio-mono") {
      void startArpeggio();
    } else if (!polyState.isReady && !polyState.isLoading) {
      void loadModel();
    }
  }, [
    loadModel,
    mission.evalMode,
    polyState.isLoading,
    polyState.isReady,
    startArpeggio,
  ]);

  const activeArpeggio = EM_ARPEGGIO_STEPS[arpeggioIndex];
  const successKeys = useMemo(() => {
    const set = new Set<string>();
    arpeggioHits.forEach((hit, i) => {
      if (!hit) return;
      const s = EM_ARPEGGIO_STEPS[i];
      if (s) set.add(`${s.string}-${s.fret}`);
    });
    return set;
  }, [arpeggioHits]);

  const activeNote =
    mission.evalMode === "arpeggio-mono" && phase === "practice" && activeArpeggio
      ? { string: activeArpeggio.string, fret: activeArpeggio.fret }
      : null;

  const dimNeck = phase === "intro";

  return (
    <SongLearnCanvasBackdrop ambient="lite" className="flex min-h-0 flex-1 flex-col w-full">
      <div
        className={`flex h-full min-h-0 flex-col ${
          pathMode ? "w-full max-w-[min(100%,1600px)] mx-auto" : "w-full"
        }`}
      >
        <header className="mx-2 mt-2 flex shrink-0 items-center justify-between gap-3 rounded-2xl border border-white/[0.08] bg-black/20 px-4 py-3 backdrop-blur-sm sm:mx-4">
          <div className="flex min-w-0 items-center gap-3">
            <button
              type="button"
              onClick={() => {
                stopArpeggio();
                onBack();
              }}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-colors hover:bg-white/5"
              style={{ borderColor: PATH.cardBorder, color: PATH.textMuted }}
              aria-label="Volver"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <div className="min-w-0">
              {pathMode && (
                <p className="mb-0.5 text-[10px] uppercase tracking-[0.14em] text-[#c4b5fd]/70">
                  {mission.eyebrow}
                </p>
              )}
              <h1 className="truncate font-serif text-sm font-light sm:text-base">
                {mission.title}
              </h1>
              <p className="truncate text-[11px] text-white/45">
                {diagram.label} ·{" "}
                {mission.evalMode === "arpeggio-mono" ? "Arpegio mono" : "Rasgueo"}
              </p>
            </div>
          </div>
          <StudioTunerControl compact />
        </header>

        <div className="relative mx-2 mb-2 mt-2 flex min-h-[min(58vh,520px)] flex-1 flex-col overflow-hidden rounded-2xl sm:mx-4">
          <div
            className={`flex flex-1 items-center justify-center p-2 transition-opacity duration-500 ${
              dimNeck ? "opacity-35" : "opacity-100"
            }`}
          >
            <div
              className={`relative flex h-full w-full items-center justify-center ${BEGINNER_PATH_STUDIO_NECK_CLASS}`}
              style={{
                transform: "scale(1.15)",
                transformOrigin: "center center",
              }}
            >
              <PracticeSurface
                className="h-full w-full"
                highlightedPositions={diagram.positions}
                mutedStrings={diagram.mutedStrings}
                interactive={false}
                showNoteNames
                showDegrees={false}
                showFingerBadges
                stringOrder="tab"
                chordNutTypography
                exploreChordNodes
                visualStyle="lutherie"
                activeNote={activeNote}
                successPositions={
                  mission.evalMode === "arpeggio-mono" ? successKeys : undefined
                }
              />
            </div>
          </div>

          {phase === "intro" && (
            <ModuleIntroModal
              title={mission.title}
              subtitle={mission.subtitle}
              description={mission.goal}
              instructions={[...mission.instructions, mission.professorTip]}
              tags={[diagram.label, mission.evalMode === "arpeggio-mono" ? "Rayos X" : "Rasgueo"]}
              timeEstimateMin={mission.timeEstimateMin}
              accent="#c4b5fd"
              accentSoft="rgba(196, 181, 253, 0.12)"
              accentGlow="rgba(196, 181, 253, 0.22)"
              borderHover={PATH.cardBorderLavender}
              textMain={PATH.text}
              textMuted={PATH.textMuted}
              textDim={PATH.textDim}
              wasCompletedBefore={wasCompletedBefore}
              beginLabel={
                mission.evalMode === "arpeggio-mono"
                  ? "Empezar auditoría"
                  : "Ver mástil y practicar"
              }
              onBegin={beginPractice}
            />
          )}

          {phase === "practice" && mission.evalMode === "strum-poly" && (
            <div className="absolute bottom-4 left-1/2 z-30 w-[min(92%,480px)] -translate-x-1/2">
              <div
                className="rounded-2xl border px-4 py-4"
                style={{
                  background: "rgba(16,15,14,0.92)",
                  borderColor: PATH.cardBorderLavender,
                  boxShadow: "0 12px 40px rgba(0,0,0,0.45)",
                }}
              >
                <p className="mb-3 text-center text-xs text-white/45">
                  Escucha el modelo, arma el acorde y rasguea con{" "}
                  <span className="text-white/70">Tocar</span>.
                </p>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <button
                    type="button"
                    onClick={handleListenSynth}
                    className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-white/20 py-3 text-sm font-medium text-white/85 hover:bg-white/5"
                  >
                    <Volume2 className="h-4 w-4" />
                    Escuchar
                  </button>
                  <button
                    type="button"
                    disabled={isRecording || polyState.isLoading}
                    onClick={() => void handleStrumDetect()}
                    className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl py-3 text-sm font-semibold text-[#100f0e] disabled:opacity-50"
                    style={{ background: PATH.btnLavender }}
                  >
                    <Mic className="h-4 w-4" />
                    {isRecording
                      ? "Escuchando…"
                      : polyState.isLoading
                        ? "Cargando…"
                        : "Tocar"}
                  </button>
                </div>
                {strumScore && !strumScore.passed && (
                  <p className="mt-3 text-center text-xs text-amber-200/80">
                    {strumScore.matchedCount}/{strumScore.needed} notas clave ·{" "}
                    {mission.feedbackError}
                  </p>
                )}
                {(micError || polyState.error) && (
                  <p className="mt-2 text-center text-xs text-red-300">
                    {micError || polyState.error}
                  </p>
                )}
              </div>
            </div>
          )}

          {phase === "practice" && mission.evalMode === "arpeggio-mono" && (
            <div className="absolute bottom-4 left-1/2 z-30 w-[min(92%,520px)] -translate-x-1/2">
              <div
                className="rounded-2xl border px-4 py-4"
                style={{
                  background: "rgba(16,15,14,0.92)",
                  borderColor: PATH.cardBorderLavender,
                  boxShadow: "0 12px 40px rgba(0,0,0,0.45)",
                }}
              >
                <p className="mb-3 text-center text-sm text-white/70">
                  Toca solo:{" "}
                  <span className="font-medium text-[#c4b5fd]">
                    {activeArpeggio?.label}
                  </span>
                </p>
                <div className="mb-3 flex flex-wrap justify-center gap-1.5">
                  {EM_ARPEGGIO_STEPS.map((step, i) => (
                    <button
                      key={step.label}
                      type="button"
                      onClick={() => playFret(step.string, step.fret, 600)}
                      className={`h-8 min-w-[2rem] rounded-lg border px-2 font-mono text-[10px] transition-colors ${
                        arpeggioHits[i]
                          ? "border-emerald-400/50 bg-emerald-400/15 text-emerald-300"
                          : i === arpeggioIndex
                            ? "border-[#c4b5fd]/60 bg-[#c4b5fd]/15 text-[#c4b5fd]"
                            : "border-white/10 text-white/30"
                      }`}
                      title="Escuchar referencia"
                    >
                      {step.note}
                      {step.octave}
                    </button>
                  ))}
                </div>
                <p className="flex items-center justify-center gap-1.5 text-[11px] text-white/40">
                  <Mic
                    className={`h-3.5 w-3.5 ${
                      listeningArpeggio ? "text-[#c4b5fd] animate-pulse" : ""
                    }`}
                  />
                  {listeningArpeggio
                    ? "Mic activo · una cuerda a la vez"
                    : "Mic detenido"}
                </p>
                {micError && (
                  <p className="mt-2 text-center text-xs text-red-300">{micError}</p>
                )}
                {!listeningArpeggio && (
                  <button
                    type="button"
                    onClick={() => void startArpeggio()}
                    className="mt-2 w-full text-xs text-[#c4b5fd]/80 underline"
                  >
                    Reanudar micrófono
                  </button>
                )}
              </div>
            </div>
          )}

          {phase === "result" && (
            <div className="absolute inset-0 z-40 flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-black/62" aria-hidden />
              <div
                className="relative z-10 w-full max-w-md rounded-2xl border px-6 py-7 text-center"
                style={{
                  background:
                    "linear-gradient(165deg, rgba(42,38,36,0.97) 0%, rgba(28,26,25,0.98) 100%)",
                  borderColor: PATH.cardBorderLavender,
                  boxShadow: "0 24px 80px rgba(0,0,0,0.55)",
                }}
              >
                <p className="mb-3 text-4xl">{passed ? "🌟" : "🔄"}</p>
                <h2 className="mb-2 font-serif text-xl text-white/90">
                  {passed ? "Misión superada" : "Aún no"}
                </h2>
                <p className="mb-5 text-sm text-white/60">
                  {passed ? mission.feedbackOk : mission.feedbackError}
                </p>
                <div className="flex flex-col gap-2 sm:flex-row">
                  {passed ? (
                    <button
                      type="button"
                      onClick={() => {
                        stopArpeggio();
                        onComplete();
                      }}
                      className="flex-1 rounded-xl py-3 text-sm font-semibold text-[#100f0e]"
                      style={{ background: PATH.btnLavender }}
                    >
                      Continuar
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setPhase("intro");
                        setStrumScore(null);
                      }}
                      className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-white/20 py-3 text-sm font-semibold text-white/85 hover:bg-white/5"
                    >
                      <RotateCcw className="h-4 w-4" />
                      Reintentar
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      stopArpeggio();
                      onBack();
                    }}
                    className="flex-1 rounded-xl py-3 text-sm text-white/50 hover:text-white/80"
                  >
                    Volver a misiones
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </SongLearnCanvasBackdrop>
  );
}
